import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  AddUpdateOfficeReference,
  DeleteOfficeReference,
  getBothCourtCasesDetailsByCourtRef,
  getOfficeReferenceByCourtRef,
  GetReAppealedCasesByCourtRef,
  EditCaseAssignmentByCourtRef,
  DeleteCaseAssignmentById,
  AddOrUpdateReAppeal,
  DeleteReAppealById,
} from "../../services/api/CasesApi.js";
import { getCourtTypeByCourtRef } from "../../services/api/MyClientsApi.js";
import "./CaseDetails.scss";
import "../../components/Loading/Loading.scss";
import TabViewer from "../../components/TabViewer/TabViewer";
import { WrittenFormat } from "../../components/WrittenFormat.js";
import { getLabelByCourtRef } from "../../services/api/LabelApi.js";
import {
  getCaseAssignmentToUserByCourtRef,
  getUserDataByID,
} from "../../services/api/AuthApi.js";
import { getHierarchy } from "../../services/api/HierarchyApi.js";
import { useToast } from "../../context/ToastContext";
import { useLoading } from "../../context/LoadingContext";
import { FiEdit, FiSave, FiTrash2, FiPlus, FiX } from "react-icons/fi";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal.js";
import AssignmentModal from "../../components/AssignmentModal/AssignmentModal.js";
import {
  normalizePartyNames,
  truncatePartyNames,
} from "../../components/TruncateParties.js";
import { useAuth } from "../../context/AuthContext";
import gsap from "gsap";

export default function CaseDetails() {
  const { courtref } = useParams();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const location = useLocation();
  const selectedCourtType = location.state?.selectedCourtType;
  const selectedCaseType = location.state?.selectedCaseType;
  console.log(selectedCourtType, "selectedCourtType casedetails");
  const [caseData, setCaseData] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const [casedetaillabels, setCaseDetailLabels] = useState([]);
  const [fetchedRefs, setFetchedRefs] = useState(new Set());

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Assignment modal state
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [assignmentModalMode, setAssignmentModalMode] = useState("assign"); // "assign", "edit", "delete"

  const [officeReference, setOfficeReference] = useState(null);
  const [officeRefInput, setOfficeRefInput] = useState("");
  const [isEditingOfficeRef, setIsEditingOfficeRef] = useState(false);

  // Add re-appeal state
  const [reAppealData, setReAppealData] = useState(null);
  const [appealPeriodDaysInput, setAppealPeriodDaysInput] = useState("");
  const [appealDeadlineDateInput, setAppealDeadlineDateInput] = useState("");
  const [isEditingAppeal, setIsEditingAppeal] = useState(false);

  const appealPeriodInputRef = useRef(null);
  const officeRefInputRef = useRef(null);

  const { role } = useAuth();
  const { showToast } = useToast();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    itemType: "",
    userName: "",
  });

  const [isPartyNamesExpanded, setIsPartyNamesExpanded] = useState(false);
  const partyNamesContentRef = useRef(null);
  const partyNamesContainerRef = useRef(null);

  useEffect(() => {
    if (!partyNamesContentRef.current || !partyNamesContainerRef.current)
      return;

    const content = partyNamesContentRef.current;
    const container = partyNamesContainerRef.current;

    if (isPartyNamesExpanded) {
      // Expanding animation
      gsap.fromTo(
        container,
        { height: "auto", overflow: "hidden" },
        { duration: 0.5, ease: "power2.out" }
      );
      gsap.fromTo(
        content,
        { opacity: 0.7, y: -10 },
        { opacity: 1, y: 0, duration: 0.3, delay: 0.1, ease: "power2.out" }
      );
    } else {
      // Collapsing animation
      gsap.to(content, {
        opacity: 0.7,
        y: -5,
        duration: 0.2,
        ease: "power2.in",
      });
      gsap.to(container, {
        height: "auto",
        duration: 0.4,
        ease: "power2.in",
        delay: 0.1,
      });
    }
  }, [isPartyNamesExpanded]);

  const partyNames = caseData?.case?.parties || "Parties Name Here";
  const truncatedPartyNames = truncatePartyNames(partyNames);
  const fullPartyNames = normalizePartyNames(partyNames);
  const needsPartyTruncation = truncatedPartyNames !== partyNames;

  const refreshCaseDetailLabels = async () => {
    if (!courtref) return;

    try {
      const res = await getLabelByCourtRef(courtref);
      setCaseDetailLabels(res.data);
    } catch (err) {
      console.error("Error refreshing case detail labels:", err);
      setCaseDetailLabels([]);
    }
  };

  const hasCaseData = (data) => {
    if (!data || !data.case) return false;

    const hasBasicInfo =
      data.case.parties ||
      data.case.caseDetails?.["CNR Number"] ||
      data.case.caseDetails?.["Filing Number"] ||
      data.case.caseStatus?.["Case Status"];

    return hasBasicInfo;
  };

  const refreshOfficeReference = async () => {
    try {
      const response = await getOfficeReferenceByCourtRef(courtref);
      console.log("Refreshed office ref response:", response?.data);
      const officeRef = response.data?.caseAssignment;
      if (officeRef) {
        setOfficeReference(officeRef);
        setOfficeRefInput(officeRef.officeRefNo || "");
      } else {
        setOfficeReference(null);
        setOfficeRefInput("");
      }
    } catch (err) {
      console.error("Error refreshing office reference:", err);
    }
  };

  const refreshReAppealData = async () => {
    try {
      const response = await GetReAppealedCasesByCourtRef(courtref);
      console.log("Refreshed re-appeal response:", response?.data);
      if (response?.data && Object.keys(response.data).length > 0) {
        setReAppealData(response.data);
        setAppealPeriodDaysInput(
          response.data.appealPeriodDays?.toString() || ""
        );
        setAppealDeadlineDateInput(response.data.appealDeadlineDate || "");
      } else {
        setReAppealData(null);
        setAppealPeriodDaysInput("");
        setAppealDeadlineDateInput("");
      }
    } catch (err) {
      console.error("Error refreshing re-appeal data:", err);
      setReAppealData(null);
      setAppealPeriodDaysInput("");
      setAppealDeadlineDateInput("");
    }
  };

  const refreshAssignments = async () => {
    try {
      const response = await getCaseAssignmentToUserByCourtRef(courtref);
      const assignments = response.data?.caseAssignment || [];

      const userPromises = assignments.map((assignment) =>
        getUserDataByID(assignment.user)
          .then((userRes) => ({
            id: assignment.user,
            assignmentId: assignment._id,
            name: userRes.data?.name || "Unknown User",
          }))
          .catch(() => ({
            id: assignment.user,
            assignmentId: assignment._id,
            name: "Unknown User",
          }))
      );

      const userDetails = await Promise.all(userPromises);
      setAssignedUsers(userDetails);
    } catch (err) {
      console.error("Error refreshing assignments:", err);
      setAssignedUsers([]);
    }
  };

  useEffect(() => {
    if (!courtref) return;

    getOfficeReferenceByCourtRef(courtref)
      .then((res) => {
        console.log(res?.data, "sdjlwd");
        const officeRef = res.data?.caseAssignment;
        if (officeRef) {
          setOfficeReference(officeRef);
          setOfficeRefInput(officeRef.officeRefNo || "");
        }
      })
      .catch((err) => {
        console.error("Error fetching office reference:", err);
      });
  }, [courtref]);

  useEffect(() => {
    getHierarchy()
      .then((res) => {
        const users = res.data?.users || [];
        const activeUsers = users.filter((user) => user.isActive !== false);
        setAvailableUsers(activeUsers);
      })
      .catch((err) => {
        console.error("Error fetching available users:", err);
        setAvailableUsers([]);
      });
  }, []);

  useEffect(() => {
    if (!courtref) return;

    GetReAppealedCasesByCourtRef(courtref)
      .then((res) => {
        console.log("Re-appeal data:", res?.data);
        if (res?.data && Object.keys(res.data).length > 0) {
          setReAppealData(res.data);
          setAppealPeriodDaysInput(res.data.appealPeriodDays?.toString() || "");
          setAppealDeadlineDateInput(res.data.appealDeadlineDate || "");
        } else {
          setReAppealData(null);
          setAppealPeriodDaysInput("");
          setAppealDeadlineDateInput("");
        }
      })
      .catch((err) => {
        console.error("Error fetching re-appeal data:", err);
        setReAppealData(null);
        setAppealPeriodDaysInput("");
        setAppealDeadlineDateInput("");
      });
  }, [courtref]);

  const handleSaveOfficeRef = async () => {
    if (!officeRefInput.trim()) {
      showToast("Please enter a reference number", "error");
      return;
    }

    try {
      const payload = {
        officeRefNo: officeRefInput.trim(),
        courtRef: courtref,
      };

      await AddUpdateOfficeReference(payload);
      await refreshOfficeReference();

      setIsEditingOfficeRef(false);
      showToast(
        officeReference
          ? "Office reference updated successfully!"
          : "Office reference added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving office reference:", error);
      showToast("Failed to save office reference. Please try again.", "error");
    }
  };

  const handleSaveAppeal = async () => {
    if (!appealPeriodDaysInput.trim()) {
      showToast("Please enter appeal period days", "error");
      return;
    }

    const appealPeriodDays = parseInt(appealPeriodDaysInput.trim());
    if (isNaN(appealPeriodDays) || appealPeriodDays <= 0) {
      showToast("Please enter a valid number for appeal period days", "error");
      return;
    }

    // Auto-calculate deadline based on decision date and appeal period
    const decisionDate = caseData?.case.caseStatus?.["Decision Date"];
    const calculatedDeadline = calculateAppealDeadline(
      decisionDate,
      appealPeriodDays
    );

    if (!calculatedDeadline && !appealDeadlineDateInput.trim()) {
      showToast(
        "Could not calculate deadline automatically. Please enter deadline date manually.",
        "error"
      );
      return;
    }

    try {
      const payload = {
        courtRef: courtref,
        appealPeriodDays: appealPeriodDays,
        appealDeadlineDate:
          calculatedDeadline || appealDeadlineDateInput.trim(),
      };

      await AddOrUpdateReAppeal(payload);
      await refreshReAppealData();

      setIsEditingAppeal(false);
      showToast(
        reAppealData
          ? "Appeal information updated successfully!"
          : "Appeal information added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving appeal information:", error);
      showToast(
        "Failed to save appeal information. Please try again.",
        "error"
      );
    }
  };

  const handleAppealPeriodChange = (e) => {
    const value = e.target.value;
    setAppealPeriodDaysInput(value);

    // Auto-calculate deadline when appeal period changes
    if (value && !isNaN(parseInt(value))) {
      const decisionDate = caseData?.case.caseStatus?.["Decision Date"];
      const calculatedDeadline = calculateAppealDeadline(
        decisionDate,
        parseInt(value)
      );
      if (calculatedDeadline) {
        setAppealDeadlineDateInput(calculatedDeadline);
      }
    }
  };

  const handleDeleteOfficeRef = () => {
    setDeleteModal({
      isOpen: true,
      itemId: officeReference._id,
      itemType: "office reference",
      userName: "",
    });
  };

  const handleDeleteAppeal = () => {
    setDeleteModal({
      isOpen: true,
      itemId: reAppealData._id,
      itemType: "appeal information",
      userName: "",
    });
  };

  // New modal handlers
  const handleOpenAssignmentModal = (mode = "assign") => {
    setAssignmentModalMode(mode);
    setIsAssignmentModalOpen(true);
  };

  const handleCloseAssignmentModal = () => {
    setIsAssignmentModalOpen(false);
    setAssignmentModalMode("assign");
  };

  const handleModalSaveAssignments = async (selectedUserIds) => {
    try {
      const payload = {
        courtRef: [courtref],
        users: selectedUserIds,
      };

      await EditCaseAssignmentByCourtRef(payload);
      await refreshAssignments();
      showToast("Case assignments updated successfully!", "success");
    } catch (error) {
      console.error("Error updating assignments:", error);
      showToast("Failed to update assignments. Please try again.", "error");
    }
  };

  const handleModalDeleteUser = (assignmentId, userName) => {
    setDeleteModal({
      isOpen: true,
      itemId: assignmentId,
      itemType: "individual assignment",
      userName: userName,
    });
  };

  const handleModalDeleteAll = () => {
    setDeleteModal({
      isOpen: true,
      itemId: courtref,
      itemType: "all assignments",
      userName: "",
    });
  };

  const confirmDeleteOfficeRef = async () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      await DeleteOfficeReference(officeReference._id);
      await refreshOfficeReference();

      setIsEditingOfficeRef(false);
      showToast("Office reference deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting office reference:", error);
      showToast(
        "Failed to delete office reference. Please try again.",
        "error"
      );
    }
  };

  const confirmDeleteAppeal = async () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      await DeleteReAppealById(reAppealData._id);
      await refreshReAppealData();

      setIsEditingAppeal(false);
      showToast("Appeal information deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting appeal information:", error);
      showToast(
        "Failed to delete appeal information. Please try again.",
        "error"
      );
    }
  };

  const confirmDeleteAssignments = async () => {
    const { itemId, itemType } = deleteModal;
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });

    try {
      if (itemType === "individual assignment") {
        await DeleteCaseAssignmentById(itemId);
        await refreshAssignments();
        showToast("Assignment deleted successfully!", "success");
      } else {
        const assignmentPromises = assignedUsers.map((user) =>
          DeleteCaseAssignmentById(user.assignmentId)
        );
        await Promise.all(assignmentPromises);
        await refreshAssignments();
        showToast("All case assignments deleted successfully!", "success");
      }
    } catch (error) {
      console.error("Error deleting assignments:", error);
      showToast("Failed to delete assignments. Please try again.", "error");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, itemId: null, itemType: "", userName: "" });
  };

  const handleEditOfficeRef = () => {
    setIsEditingOfficeRef(true);
  };

  const handleEditAppeal = () => {
    setIsEditingAppeal(true);
  };

  useEffect(() => {
    if (isEditingOfficeRef && officeRefInputRef.current) {
      setTimeout(() => {
        try {
          officeRefInputRef.current.focus();
        } catch (e) {}
      }, 50);
    }
  }, [isEditingOfficeRef]);

  useEffect(() => {
    if (isEditingAppeal && appealPeriodInputRef.current) {
      setTimeout(() => {
        try {
          appealPeriodInputRef.current.focus();
          appealPeriodInputRef.current.select &&
            appealPeriodInputRef.current.select();
        } catch (e) {
          // ignore focus errors
        }
      }, 50);
    }
  }, [isEditingAppeal]);

  const handleCancelEdit = () => {
    setIsEditingOfficeRef(false);
    setOfficeRefInput(officeReference?.officeRefNo || "");
  };

  const handleCancelEditAppeal = () => {
    setIsEditingAppeal(false);
    setAppealPeriodDaysInput(reAppealData?.appealPeriodDays?.toString() || "");
    setAppealDeadlineDateInput(reAppealData?.appealDeadlineDate || "");
  };

  const handleEditCase = async () => {
    try {
      showLoading("Loading form...");
      const courtTypeResponse = await getCourtTypeByCourtRef(courtref);
      const courtSource = courtTypeResponse?.data?.source;

      let courtType = "other"; // default

      if (courtSource) {
        // Map the API response source to court types
        switch (courtSource.toLowerCase()) {
          case "highcourt":
          case "high court":
            courtType = "high";
            break;
          case "districtcourt":
          case "district court":
          case "district":
            courtType = "district";
            break;
          case "othercourt":
          case "other court":
          case "other":
            courtType = "other";
            break;
          default:
            // If source contains 'high', it's high court
            if (courtSource.toLowerCase().includes("high")) {
              courtType = "high";
            } else if (courtSource.toLowerCase().includes("district")) {
              courtType = "district";
            } else {
              courtType = "other";
            }
        }
      }

      // Navigate to the edit form with the court type and court reference
      navigate(`/home/cases/editcase/${courtType}/${courtref}`);
    } catch (error) {
      console.error("Error determining court type for edit:", error);
      showToast("Failed to determine court type. Please try again.", "error");
    } finally {
      hideLoading();
    }
  };

  const calculateAppealDeadline = (decisionDate, appealPeriodDays) => {
    if (!decisionDate || !appealPeriodDays) return "";

    try {
      // Parse the decision date (assuming format like "08th October 2021")
      const dateStr = decisionDate.replace(/(\d+)(st|nd|rd|th)/, "$1");
      const date = new Date(dateStr);

      if (isNaN(date.getTime())) return "";

      // Add appeal period days
      date.setDate(date.getDate() + parseInt(appealPeriodDays));

      // Format back to similar format
      const day = date.getDate();
      const suffix =
        day === 1 || day === 21 || day === 31
          ? "st"
          : day === 2 || day === 22
          ? "nd"
          : day === 3 || day === 23
          ? "rd"
          : "th";

      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      return `${day}${suffix} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (error) {
      console.error("Error calculating appeal deadline:", error);
      return "";
    }
  };

  const isWithin60DaysOfHearing = (hearingDate) => {
    if (!hearingDate) return false;

    try {
      // Parse the hearing date (assuming format like "08th October 2021")
      const dateStr = hearingDate.replace(/(\d+)(st|nd|rd|th)/, "$1");
      const hearing = new Date(dateStr);

      if (isNaN(hearing.getTime())) return false;

      const today = new Date();
      const daysDifference = Math.floor(
        (today - hearing) / (1000 * 60 * 60 * 24)
      );

      return daysDifference <= 60 && daysDifference >= 0; // Within 60 days after hearing
    } catch (error) {
      console.error("Error checking hearing date:", error);
      return false;
    }
  };

  useEffect(() => {
    if (courtref) {
      showLoading("Loading case details...");
      setHasError(false);
      getBothCourtCasesDetailsByCourtRef(courtref)
        .then((res) => {
          setCaseData(res?.data);
          console.log(res, "caseesss");

          if (!hasCaseData(res?.data)) {
            setHasError(true);
          }
        })
        .catch((err) => {
          console.error("Error fetching case details:", err);
          setHasError(true);
        })
        .finally(() => hideLoading());
    }
  }, [courtref]);

  useEffect(() => {
    if (!courtref || fetchedRefs.has(courtref)) return;

    getLabelByCourtRef(courtref)
      .then((res) => {
        setCaseDetailLabels(res.data);
        setFetchedRefs((prev) => new Set(prev).add(courtref));
      })
      .catch((err) => {
        console.error("Error fetching case detail labels:", err);
        setCaseDetailLabels([]);
      });
  }, [courtref, fetchedRefs]);

  useEffect(() => {
    if (!courtref) return;

    refreshAssignments();
  }, [courtref]);

  if (!courtref) return <p>Error: Court reference not provided.</p>;
  if (!caseData) return null;
  if (hasError || !hasCaseData(caseData)) {
    return (
      <div className="case-detail-container">
        <div className="no-data-message">
          <h3>No Data Available</h3>
          <p>No case data is available for this court reference.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="case-detail-container">
      <div className="case-detail-header">
        <div className="caseheader-label">
          <div
            className="expandable-case-party-names"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <div
              ref={partyNamesContainerRef}
              style={{
                overflow: isPartyNamesExpanded ? "visible" : "hidden",
                // flex: 1,
                minWidth: 0,
              }}
            >
              <h3
                ref={partyNamesContentRef}
                style={{
                  margin: 0,
                  display: "inline",
                  lineHeight: "1.4",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {isPartyNamesExpanded ? fullPartyNames : truncatedPartyNames}
              </h3>
            </div>

            {needsPartyTruncation && (
              <button
                onClick={() => setIsPartyNamesExpanded(!isPartyNamesExpanded)}
                className="case-party-see-more-btn"
                onMouseEnter={(e) => {
                  gsap.to(e.target, {
                    scale: 1.05,
                    backgroundColor: "#004A9A",
                    color: "white",
                    duration: 0.2,
                    ease: "power2.out",
                  });
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    scale: 1,
                    backgroundColor: "transparent",
                    color: "#004A9A",
                    duration: 0.2,
                    ease: "power2.out",
                  });
                }}
              >
                {isPartyNamesExpanded ? "See Less" : "See More"}
              </button>
            )}
          </div>

          {casedetaillabels.map((label, index) => (
            <span
              key={index}
              className="casedetail-label-pill"
              style={{ backgroundColor: label.color }}
            >
              {label.name}
            </span>
          ))}
        </div>
        {selectedCaseType === "added" && (
          <button className="casedetail-edit-btn" onClick={handleEditCase}>
            ✎ Edit Case
          </button>
        )}
      </div>
      <div className="casedata-details">
        <div className="case-info-grid">
          <div>
            <strong>CNR:</strong>{" "}
            {caseData?.case?.caseDetails?.["CNR Number"] || "N/A"}
          </div>
          {caseData.case.caseStatus?.["Next Hearing Date"] ? (
            <div>
              <strong>Next Hearing Date:</strong>{" "}
              {caseData?.case.caseStatus?.["Next Hearing Date"] || "N/A"}
            </div>
          ) : (
            <div>
              <strong>Decision Date:</strong>{" "}
              {caseData?.case.caseStatus?.["Decision Date"] || "N/A"}
            </div>
          )}
          <div>
            <strong>File no:</strong>{" "}
            {caseData?.case.caseDetails?.["Filing Number"] || "N/A"}
          </div>
          <div>
            <strong>Date of Filing:</strong>{" "}
            {caseData?.case.caseDetails?.["Filing Date"] || "N/A"}
          </div>
          <div>
            <strong>Stage:</strong>{" "}
            {caseData?.case.caseStatus?.["Stage"] || "N/A"}
          </div>
          <div>
            <strong>State:</strong>{" "}
            {caseData?.case.caseStatus?.["State"] || "N/A"}
          </div>
          <div>
            <strong>Created On:</strong>{" "}
            {caseData?.case.caseDetails?.["Registration Date"] || "N/A"}
          </div>
          <div>
            <strong>District:</strong>{" "}
            {caseData?.case.caseStatus?.["District"] || "N/A"}
          </div>
          <div>
            <strong>Status:</strong>{" "}
            <span
              className={`case-detail-status-${caseData?.case.caseStatus?.[
                "Case Status"
              ]
                ?.toLowerCase()
                .replace(/ /g, "-")}`}
            >
              {caseData.case.caseStatus?.["Case Status"] || "Pending"}
            </span>
          </div>

          {role !== "customer" && (
            <div className="office-ref">
              <strong>Office Reference:</strong>
              <div className="case-office-ref-container">
                {!officeReference && !isEditingOfficeRef ? (
                  <button
                    className="case-add-office-ref-btn"
                    onClick={() => setIsEditingOfficeRef(true)}
                  >
                    <FiPlus size={12} /> Add Office Reference
                  </button>
                ) : isEditingOfficeRef ? (
                  <div className="case-office-ref-edit">
                    <input
                      ref={officeRefInputRef}
                      type="text"
                      value={officeRefInput}
                      onChange={(e) => setOfficeRefInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOfficeRef();
                        }
                      }}
                      placeholder="Enter office reference"
                      className="case-office-ref-input"
                    />
                    <div className="case-office-ref-actions">
                      <button
                        onClick={handleSaveOfficeRef}
                        className="case-save-icon-btn"
                      >
                        <FiSave size={12} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="case-cancel-icon-btn"
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="case-office-ref-display">
                    <span>{officeReference.officeRefNo}</span>
                    <div className="case-office-ref-actions">
                      <button
                        onClick={handleEditOfficeRef}
                        className="case-edit-icon-btn"
                      >
                        <FiEdit size={12} />
                      </button>
                      <button
                        onClick={handleDeleteOfficeRef}
                        className="case-delete-icon-btn"
                      >
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <strong>Advocates:</strong>{" "}
            {WrittenFormat(caseData?.case.advocates || "Unassigned")}
          </div>
        </div>

        <div className="case-sidebar">
          <div className="assignment-info">
            <div className="assignment-header">
              <p>
                <strong>Lawyer's Assigned:</strong>
              </p>
              {assignedUsers.length > 0 && role !== "customer" && (
                <div className="assignment-actions">
                  <button
                    onClick={() => handleOpenAssignmentModal("edit")}
                    className="case-edit-icon-btn"
                    title="Edit assignments"
                  >
                    <FiEdit size={12} />
                  </button>
                  <button
                    onClick={() => handleOpenAssignmentModal("delete")}
                    className="case-delete-icon-btn"
                    title="Manage assignments"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              )}
            </div>

            <div className="assigned-names">
              {assignedUsers.length > 0 ? (
                assignedUsers.map((user, index) => (
                  <span key={index}>
                    <span
                      className="user-link"
                      onClick={() =>
                        (window.location.href = `/home/addusers/${user.id}`)
                      }
                    >
                      {user.name}
                    </span>
                    {index < assignedUsers.length - 1 && ", "}
                  </span>
                ))
              ) : (
                <div className="unassigned-container">
                  <span>Unassigned</span>
                  <button
                    onClick={() => handleOpenAssignmentModal("assign")}
                    className="case-add-assignment-btn"
                  >
                    <FiPlus size={12} /> Assign Lawyer
                  </button>
                </div>
              )}
            </div>
          </div>

          {caseData.case.caseStatus?.["First Hearing Date"] ? (
            <p>
              <strong>First Hearing Date:</strong>{" "}
              {caseData.case.caseStatus?.["First Hearing Date"] || "N/A"}
            </p>
          ) : (
            <div className="casedecison">
              <p>
                <strong>Decision Date:</strong>
              </p>
              <p className="casedetaildate">
                {caseData.case.caseStatus?.["Decision Date"] || "N/A"}
              </p>
            </div>
          )}

          {/* Appeal Information Section */}
          {/* <div className="appeal-info">
            <div className="appeal-header">
              <p>
                <strong>Appeal Information:</strong>
              </p>
              {!isEditingAppeal && reAppealData && (
                <div className="appeal-actions">
                  <button
                    onClick={handleEditAppeal}
                    className="case-edit-icon-btn"
                    title="Edit appeal information"
                  >
                    <FiEdit size={12} />
                  </button>
                  <button
                    onClick={handleDeleteAppeal}
                    className="case-delete-icon-btn"
                    title="Delete appeal information"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              )}
            </div>

            {!reAppealData && !isEditingAppeal ? (
              <button
                className="case-add-appeal-btn"
                onClick={() => setIsEditingAppeal(true)}
              >
                <FiPlus size={12} /> Add Appeal Info
              </button>
            ) : isEditingAppeal ? (
              // <div className="appeal-edit-container">
              //   <div className="appeal-input-group">
              //     <input
              //       type="number"
              //       value={appealPeriodDaysInput}
              //       onChange={(e) => setAppealPeriodDaysInput(e.target.value)}
              //       placeholder="Appeal period days"
              //       className="case-appeal-input"
              //       min="1"
              //     />
              //     <input
              //       type="text"
              //       value={appealDeadlineDateInput}
              //       onChange={(e) => setAppealDeadlineDateInput(e.target.value)}
              //       placeholder="Appeal deadline date"
              //       className="case-appeal-input"
              //     />
              //   </div>
              //   <div className="appeal-edit-actions">
              //     <button
              //       onClick={handleSaveAppeal}
              //       className="case-save-icon-btn"
              //     >
              //       <FiSave size={12} />
              //     </button>
              //     <button
              //       onClick={handleCancelEditAppeal}
              //       className="case-cancel-icon-btn"
              //     >
              //       <FiX size={12} />
              //     </button>
              //   </div>
              // </div>
              <div className="appeal-edit-container">
                <div className="appeal-input-group">
                  <input
                    type="number"
                    ref={appealPeriodInputRef}
                    value={appealPeriodDaysInput}
                    onChange={handleAppealPeriodChange}
                    placeholder="Appeal period days"
                    className="case-appeal-input"
                    min="1"
                  />
                  <input
                    type="text"
                    value={appealDeadlineDateInput}
                    onChange={(e) => setAppealDeadlineDateInput(e.target.value)}
                    placeholder="Appeal deadline date (auto-calculated)"
                    className="case-appeal-input"
                    readOnly={!!appealPeriodDaysInput}
                  />
                  {caseData?.case.caseStatus?.["Decision Date"] && (
                    <div className="appeal-base-date">
                      <small>Base Date: {caseData.case.caseStatus["Decision Date"]}</small>
                    </div>
                  )}
                </div>
                <div className="appeal-edit-actions">
                  <button
                    onClick={handleSaveAppeal}
                    className="case-save-icon-btn"
                  >
                    <FiSave size={12} />
                  </button>
                  <button
                    onClick={handleCancelEditAppeal}
                    className="case-cancel-icon-btn"
                  >
                    <FiX size={12} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="appeal-display">
                <p>
                  <strong>Appeal Period Days:</strong>{" "}
                  {reAppealData.appealPeriodDays || "N/A"}
                </p>
                <p>
                  <strong>Appeal Deadline Date:</strong>{" "}
                  {reAppealData.appealDeadlineDate || "N/A"}
                </p>
              </div>
            )}
          </div> */}
          {/* <div className="appeal-info">
            <div className="appeal-header">
              <p>
                <strong>Appeal Information:</strong>
              </p>
              {!isEditingAppeal &&
                reAppealData &&
                isWithin60DaysOfHearing(
                  caseData?.case.caseStatus?.["Decision Date"] ||
                    caseData?.case.caseStatus?.["Next Hearing Date"]
                ) &&
                role !== "customer" && (
                  <div className="appeal-actions">
                    <button
                      onClick={handleEditAppeal}
                      className="case-edit-icon-btn"
                      title="Edit appeal information"
                    >
                      <FiEdit size={12} />
                    </button>
                    <button
                      onClick={handleDeleteAppeal}
                      className="case-delete-icon-btn"
                      title="Delete appeal information"
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                )}
            </div>

            {!reAppealData && !isEditingAppeal ? (
              isWithin60DaysOfHearing(
                caseData?.case.caseStatus?.["Decision Date"] ||
                  caseData?.case.caseStatus?.["Next Hearing Date"]
              ) ? (
                <button
                  className="case-add-appeal-btn"
                  onClick={() => setIsEditingAppeal(true)}
                >
                  <FiPlus size={12} /> Add Appeal Info
                </button>
              ) : (
                <div className="appeal-time-restriction">
                  <p
                    style={{
                      color: "#666",
                      fontSize: "0.9em",
                      fontStyle: "italic",
                    }}
                  >
                    Appeal period has expired (60 days limit)
                  </p>
                </div>
              )
            ) : isEditingAppeal &&
              isWithin60DaysOfHearing(
                caseData?.case.caseStatus?.["Decision Date"] ||
                  caseData?.case.caseStatus?.["Next Hearing Date"]
              ) ? (
              <div className="appeal-edit-container">
                <div className="appeal-input-group">
                  <input
                    type="number"
                    ref={appealPeriodInputRef}
                    value={appealPeriodDaysInput}
                    onChange={handleAppealPeriodChange}
                    placeholder="Appeal period days"
                    className="case-appeal-input"
                    min="1"
                  />
                  <input
                    type="text"
                    value={appealDeadlineDateInput}
                    onChange={(e) => setAppealDeadlineDateInput(e.target.value)}
                    placeholder="Appeal deadline date (auto-calculated)"
                    className="case-appeal-input"
                    readOnly={!!appealPeriodDaysInput}
                  />
                  {caseData?.case.caseStatus?.["Decision Date"] && (
                    <div className="appeal-base-date">
                      <small>
                        Base Date: {caseData.case.caseStatus["Decision Date"]}
                      </small>
                    </div>
                  )}
                </div>
                <div className="appeal-edit-actions">
                  <button
                    onClick={handleSaveAppeal}
                    className="case-save-icon-btn"
                  >
                    <FiSave size={12} />
                  </button>
                  <button
                    onClick={handleCancelEditAppeal}
                    className="case-cancel-icon-btn"
                  >
                    <FiX size={12} />
                  </button>
                </div>
              </div>
            ) : reAppealData ? (
              <div className="appeal-display">
                <p>
                  <strong>Appeal Period Days:</strong>{" "}
                  {reAppealData.appealPeriodDays || "N/A"}
                </p>
                <p>
                  <strong>Appeal Deadline Date:</strong>{" "}
                  {reAppealData.appealDeadlineDate || "N/A"}
                </p>
                {!isWithin60DaysOfHearing(
                  caseData?.case.caseStatus?.["Decision Date"] ||
                    caseData?.case.caseStatus?.["Next Hearing Date"]
                ) && (
                  <p
                    style={{
                      color: "#666",
                      fontStyle: "italic",
                      marginTop: "8px",
                    }}
                  >
                    (Appeal period has expired - read only)
                  </p>
                )}
              </div>
            ) : null}
          </div> */}
        </div>
      </div>

      <div className="appeal-info">
        <div className="appeal-data">
          <div className="appeal-header">
            {!isEditingAppeal &&
              reAppealData &&
              isWithin60DaysOfHearing(
                caseData?.case.caseStatus?.["Decision Date"] ||
                  caseData?.case.caseStatus?.["Next Hearing Date"]
              ) &&
              role !== "customer" && (
                <div className="appeal-actions">
                  <button
                    onClick={handleEditAppeal}
                    className="case-edit-icon-btn"
                    title="Edit appeal information"
                  >
                    <FiEdit size={12} />
                  </button>
                  <button
                    onClick={handleDeleteAppeal}
                    className="case-delete-icon-btn"
                    title="Delete appeal information"
                  >
                    <FiTrash2 size={12} />
                  </button>
                </div>
              )}
          </div>

          {!reAppealData && !isEditingAppeal ? (
            isWithin60DaysOfHearing(
              caseData?.case.caseStatus?.["Decision Date"] ||
                caseData?.case.caseStatus?.["Next Hearing Date"]
            ) ? (
              <button
                className="case-add-appeal-btn"
                onClick={() => setIsEditingAppeal(true)}
              >
                <FiPlus size={12} /> Add Appeal Info
              </button>
            ) : (
              <div className="appeal-time-restriction">
                <p
                  style={{
                    color: "#666",
                    fontSize: "0.9em",
                    fontStyle: "italic",
                  }}
                >
                  Appeal period has expired (60 days limit)
                </p>
              </div>
            )
          ) : isEditingAppeal &&
            isWithin60DaysOfHearing(
              caseData?.case.caseStatus?.["Decision Date"] ||
                caseData?.case.caseStatus?.["Next Hearing Date"]
            ) ? (
            <div className="appeal-edit-container">
              <div className="appeal-input-group">
                <input
                  type="number"
                  ref={appealPeriodInputRef}
                  value={appealPeriodDaysInput}
                  onChange={handleAppealPeriodChange}
                  placeholder="Appeal period days"
                  className="case-appeal-input"
                  min="1"
                />
                <input
                  type="text"
                  value={appealDeadlineDateInput}
                  onChange={(e) => setAppealDeadlineDateInput(e.target.value)}
                  placeholder="Appeal deadline date (auto-calculated)"
                  className="case-appeal-input"
                  readOnly={!!appealPeriodDaysInput}
                />
                {caseData?.case.caseStatus?.["Decision Date"] && (
                  <div className="appeal-base-date">
                    <small>
                      Base Date: {caseData.case.caseStatus["Decision Date"]}
                    </small>
                  </div>
                )}
              </div>
              <div className="appeal-edit-actions">
                <button
                  onClick={handleSaveAppeal}
                  className="case-save-icon-btn"
                >
                  <FiSave size={12} />
                </button>
                <button
                  onClick={handleCancelEditAppeal}
                  className="case-cancel-icon-btn"
                >
                  <FiX size={12} />
                </button>
              </div>
            </div>
          ) : reAppealData ? (
            <div className="appeal-display">
              <div className="appeal-para">
                <p>
                  <strong>Appeal Period Days:</strong>{" "}
                  {reAppealData.appealPeriodDays || "N/A"}
                </p>
                <p>
                  <strong>Appeal Deadline Date:</strong>{" "}
                  {reAppealData.appealDeadlineDate || "N/A"}
                </p>
              </div>
              {!isWithin60DaysOfHearing(
                caseData?.case.caseStatus?.["Decision Date"] ||
                  caseData?.case.caseStatus?.["Next Hearing Date"]
              ) && (
                <p
                  style={{
                    color: "#666",
                    fontStyle: "italic",
                    marginTop: "3px",
                  }}
                >
                  (Appeal period has expired - read only)
                </p>
              )}
            </div>
          ) : null}
        </div>
        <div className="appeal-information">
          <strong>Appeal Information</strong>
        </div>
      </div>

      {console.log(selectedCourtType, "selectedCourtType casedetails11")}
      {console.log(caseData?.case, "caseData?.case?.orders")}
      <TabViewer
        orderInfo={caseData?.case?.orderInfo || caseData?.case?.orders}
        caseHistory={caseData?.case?.caseHistory}
        documentsInfo={caseData?.case?.documents}
        caseData={caseData}
        selectedCourtType={selectedCourtType}
        courtref={courtref}
        refreshCaseDetailLabels={refreshCaseDetailLabels}
      />

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={isAssignmentModalOpen}
        onClose={handleCloseAssignmentModal}
        availableUsers={availableUsers}
        assignedUsers={assignedUsers}
        onSave={handleModalSaveAssignments}
        onDeleteUser={handleModalDeleteUser}
        onDeleteAll={handleModalDeleteAll}
        role={role}
        mode={assignmentModalMode}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={deleteModal.itemType}
        userName={deleteModal.userName}
        onConfirm={
          deleteModal.itemType === "office reference"
            ? confirmDeleteOfficeRef
            : deleteModal.itemType === "appeal information"
            ? confirmDeleteAppeal
            : confirmDeleteAssignments
        }
        onCancel={cancelDelete}
      />
    </div>
  );
}
